import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  ZERO,
  ZERO_QTY,
  addMoney,
  addQty,
  centavos,
  costOf,
  formatFxRate,
  formatMoney,
  fechaEnNicaragua,
  formatQuantity,
  fxRate,
  hoy,
  newId,
  parseMoney,
  parseQuantity,
  saleToBase,
  subMoney,
  subQty,
  unitCostFromTotal,
  usdToCordobas,
  type Centavos,
  type CuentaBanco,
  type CuentaBancoCordobas,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import {
  adondeVaLaCorreccion,
  corregirCantidadDeCompra,
  corregirFormaDePago,
  esDeudaAnterior,
  pendienteDeCompra,
  reencontrarElPago,
  reencontrarLosPagos,
  valorDevueltoCompra,
  type Compra,
  type FormaPago,
  type LadoDelPago,
  type PagoCorregido,
  type PorQueElPagoNo,
  type Reencontrado,
} from '@abastece/core';
import { db } from '../db/dexie.js';
import { localReader, readSettings } from '../db/reader.js';
import { cuentaEnCordobas, nombrePuesto } from '../lib/bancos.js';
import { sesionAbierta } from '../lib/caja.js';
import { unidadesDe } from '../lib/catalogo.js';
import { emitir, emitirYVer } from '../lib/emitir.js';
import { diceElConteo } from '../lib/encontrado.js';
import {
  ESTADOS_COMPRA,
  FORMAS_DE_COMPRA,
  SIN_FILTROS_COMPRAS,
  compraCoincide,
  filtrosDeCompras,
  ordenarCompras,
  rangoDe,
  resumenCompras,
  type ContextoCompras,
  type EstadoCompra,
  type FiltroCompras,
} from '../lib/historial.js';
import { usePuede } from '../lib/sesion.js';
import { Campo, Selector } from '../ui/Campo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { SaleDeLaGaveta, faltaEnGaveta, useGaveta } from '../ui/SaleDeLaGaveta.js';
import { BuscarEnLista, Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico, type NombreIcono } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { PreciosBajoMargen, buscarPreciosBajos } from '../ui/PreciosBajoMargen.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { enRango } from '../lib/costos.js';

const PAGO: Record<string, Clave> = {
  efectivo_cordobas: 'pago.efectivo_cordobas',
  efectivo_dolares: 'gastos.efectivoUsd',
  transferencia: 'pago.transferencia',
  billetera: 'pago.billetera',
  credito: 'pago.credito',
};

/** How many purchases show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 50;

export function Compras() {
  const t = useT();
  const nombre = useNombreProducto();
  const hoyEs = hoy();
  const compras = useLiveQuery(() => db.compras.toArray(), [], undefined);
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const puede = usePuede('recibir_compras');
  const puedeCorregir = usePuede('configurar');
  const [filtro, setFiltro] = useState<FiltroCompras>(SIN_FILTROS_COMPRAS);
  const [cuantas, setCuantas] = useState(LOTE);
  /** An id, not the purchase: the detail reads it live, so a return or a corrected cost shows at once. */
  const [abierta, setAbierta] = useState<string | null>(null);
  const [anulando, setAnulando] = useState<string | null>(null);
  const [devolviendo, setDevolviendo] = useState<Compra | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<Compra | null>(null);
  const [contando, setContando] = useState<Compra | null>(null);
  const [pagando, setPagando] = useState<Compra | null>(null);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDeCompras(filtro, rango);
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroCompras>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantas(LOTE);
  };

  const contexto = useMemo<ContextoCompras>(
    () => ({
      proveedores: new Map(proveedores.map((p) => [p.id, p.nombre])),
      productos: new Map(productos.map((p) => [p.id, p])),
    }),
    [proveedores, productos],
  );
  // What was owed from before brought no goods: it is in «Cuentas por pagar», under «Gastos y pagos».
  const conMercaderia = useMemo(() => (compras ?? []).filter((c) => !esDeudaAnterior(c)), [compras]);
  const encontradas = useMemo(
    () =>
      ordenarCompras(filtrando ? conMercaderia.filter((c) => compraCoincide(c, filtro, rango, contexto)) : conMercaderia, filtro.texto),
    // `rango` is worked out afresh each time: its two days are what it is.
    [conMercaderia, filtrando, filtro, rango.desde, rango.hasta, contexto],
  );
  const mostradas = encontradas.slice(0, cuantas);
  const resumen = resumenCompras(encontradas);
  const nombreProveedor = (id: string) => contexto.proveedores.get(id) ?? t('gastos.proveedor');
  /** What came, by name, on the card: what a search by product is checked against. */
  const loQueTrajo = (c: Compra) => {
    const nombres = c.lineas.map((l) => {
      const p = contexto.productos.get(l.productoId);
      return p ? nombre(p) : '—';
    });
    return [...new Set(nombres)].join(', ');
  };

  /** The purchase «Anular esta compra» was tapped on. */
  const anulada = anulando ? compras?.find((c) => c.id === anulando) : undefined;

  async function anular(compraId: string) {
    if (!motivo.trim()) return;
    setError(null);
    try {
      // Refused once the money it paid with was counted since (`sinContar`): said, with what to do.
      const { rechazo } = await emitirYVer('compra.anulada', { compraId, motivo: motivo.trim(), sinContar: true });
      // The reducer may refuse — a later movement makes an annulment impossible — and says why.
      const despues = await db.compras.get(compraId);
      if (rechazo || !despues?.anulada) {
        setError(rechazo ?? t('compras.noSePudoAnular'));
        return;
      }
      setAnulando(null);
      setMotivo('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('compras.noSePudoAnular'));
    }
  }

  return (
    <Pagina
      titulo={t('compras.titulo')}
      subtitulo={t('compras.registradas', { n: conMercaderia.filter((c) => !c.anulada).length })}
      acciones={
        puede && (
          <Link to="/compras/nueva" className="btn-primario gap-1.5 px-4 text-sm">
            <Ico icono="recibir" className="size-4" />
            {t('compra.nueva')}
          </Link>
        )
      }
    >
      {/* A box over nothing at all would only be a question with no answer. */}
      {(filtrando || conMercaderia.length > 0) && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('historial.buscarCompras') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_COMPRAS, texto: filtro.texto })}
        >
          <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />
          <BuscarEnLista
            clave="proveedorId"
            etiqueta={t('compra.proveedor')}
            placeholder={t('historial.buscarProveedor')}
            opciones={proveedores}
            value={filtro.proveedorId}
            onChange={(proveedorId) => cambiar({ proveedorId })}
          />
          <FilaDesplegables>
            <Desplegable
              clave="forma"
              etiqueta={t('historial.forma')}
              value={filtro.forma}
              onChange={(v) => cambiar({ forma: v as FormaPago | '' })}
              elegido={filtro.forma ? t(PAGO[filtro.forma]!) : undefined}
            >
              <option value="">{t('historial.todas')}</option>
              {FORMAS_DE_COMPRA.map((f) => (
                <option key={f} value={f}>
                  {t(PAGO[f]!)}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="estado"
              etiqueta={t('historial.estado')}
              value={filtro.estado}
              onChange={(v) => cambiar({ estado: v as EstadoCompra })}
              elegido={filtro.estado === 'todas' ? undefined : t(`historial.estado.${filtro.estado}`)}
            >
              {ESTADOS_COMPRA.map((e) => (
                <option key={e} value={e}>
                  {t(`historial.estado.${e}`)}
                </option>
              ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && encontradas.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('historial.encontradas', { n: resumen.n, total: formatMoney(resumen.total) })}
        </p>
      )}

      {/* Nothing said while it is read: «nothing matches» was only «not read yet». */}
      {compras === undefined ? null : mostradas.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('historial.ningunaCompra')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_COMPRAS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t('compras.ninguna')} icono="compras">
            {puede && (
              <Link to="/compras/nueva" className="btn-primario">
                <Ico icono="recibir" />
                {t('compras.registrarPrimera')}
              </Link>
            )}
          </Vacio>
        )
      ) : (
        <ul className="flex flex-col gap-2" data-compras-lista>
          {mostradas.map((c) => (
            <li key={c.id} className={`tarjeta ${c.anulada ? 'opacity-50' : ''}`} data-compra={c.id}>
              {/* The purchase opens on what it brought (the owner, 2026-10-05); its actions stay below. */}
              <button
                type="button"
                onClick={() => setAbierta(c.id)}
                data-sin-icono="fila"
                className="tarjeta-abrir flex w-full items-start justify-between gap-4 text-left"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {nombreProveedor(c.proveedorId)}
                    {c.documento && <span className="ml-2 text-xs text-slate-500">{c.documento}</span>}
                  </p>
                  <p className="truncate text-sm text-slate-300" data-compra-productos>
                    {loQueTrajo(c)}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t('compras.resumen', {
                      fecha: c.fecha,
                      n: c.lineas.length,
                      pago: (c.pagos ?? []).some((x) => x.conLaCompra)
                        ? t('compras.enPartes')
                        : PAGO[c.formaPago]
                          ? t(PAGO[c.formaPago]!)
                          : c.formaPago,
                    })}
                    {c.moneda === 'USD' &&
                      c.tasaCambio &&
                      t('compras.tasaUsd', { tasa: formatFxRate(c.tasaCambio as never) })}
                  </p>
                  {c.anulada && (
                    <p className="mt-1 text-xs text-rose-400">
                      {t('compras.anulada', { motivo: c.motivoAnulacion ?? '' })}
                    </p>
                  )}
                  {c.vencePago && !c.anulada && pendienteDeCompra(c) > 0 && (
                    <p className="mt-1 text-xs text-amber-300">
                      {t('compras.vence', { fecha: c.vencePago })}
                    </p>
                  )}
                  {(c.devuelto ?? 0) > 0 && (
                    <p className="tabular mt-1 text-xs text-sky-300">
                      {t('devolucionCompra.devuelto', { monto: formatMoney(c.devuelto!) })}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular font-semibold">{formatMoney(c.totalCordobas)}</p>
                  {c.flete > 0 && (
                    <p className="tabular mt-0.5 text-xs text-slate-500">
                      {t('compras.flete', { monto: formatMoney(c.flete, { symbol: false }) })}
                    </p>
                  )}
                  {(c.iva ?? 0) > 0 && (
                    <p className="tabular mt-0.5 text-xs text-slate-500">
                      {t('compras.iva', { monto: formatMoney(c.iva!, { symbol: false }) })}
                    </p>
                  )}
                </div>
              </button>
              {/* The card's whole width: beside the total, «Transferencia» ran into its amount. */}
              <PagadoEnPartes compra={c} />

              {puede && !c.anulada && (
                <div className="mt-3 border-t border-slate-800 pt-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {c.lineas.some((l) => subQty(l.cantidadBase, l.devuelta ?? ZERO_QTY) > 0) && (
                      <button
                        type="button"
                        className="btn-texto gap-1 text-xs text-slate-300"
                        onClick={() => setDevolviendo(c)}
                      >
                        <Ico icono="devolver" className="size-3.5" />
                        {t('devolucionCompra.devolver')}
                      </button>
                    )}
                    {/* What a correction changes is money already counted: the owner's (`configurar`). */}
                    {puedeCorregir && c.lineas.some((l) => (l.devuelta ?? 0) === 0) && (
                      <button
                        type="button"
                        className="btn-texto gap-1 text-xs text-slate-300"
                        onClick={() => setCorrigiendo(c)}
                      >
                        <Ico icono="editar" className="size-3.5" />
                        {t('corregir.boton')}
                      </button>
                    )}
                    {/* A sack typed as one unit: what each unit cost, the owner's as the cost is (2026-10-13). */}
                    {puedeCorregir && c.lineas.some((l) => (l.devuelta ?? 0) === 0) && (
                      <button
                        type="button"
                        className="btn-texto gap-1 text-xs text-slate-300"
                        data-corregir-cantidad
                        onClick={() => setContando(c)}
                      >
                        <Ico icono="contar" className="size-3.5" />
                        {t('cantidad.boton')}
                      </button>
                    )}
                    {/* Paid another way than it says: money already counted, the owner's (2026-10-14). */}
                    {puedeCorregir && (
                      <button
                        type="button"
                        className="btn-texto gap-1 text-xs text-slate-300"
                        data-corregir-pago
                        onClick={() => setPagando(c)}
                      >
                        <Ico icono="pagar" className="size-3.5" />
                        {t('pagoCorregido.boton')}
                      </button>
                    )}
                    {/* Once something went back, putting the purchase back as it was would
                        undo that too. */}
                    {(c.devuelto ?? 0) === 0 && c.lineas.every((l) => (l.devuelta ?? 0) === 0) && (
                      <button
                        type="button"
                        className="btn-texto gap-1 text-xs text-slate-500"
                        onClick={() => {
                          setAnulando(c.id);
                          setMotivo('');
                          setError(null);
                        }}
                      >
                        <Ico icono="anular" className="size-3.5" />
                        {t('compras.anularEsta')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {encontradas.length > cuantas && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantas((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}

      {abierta && <DetalleCompra compraId={abierta} onCerrar={() => setAbierta(null)} />}
      {devolviendo && (
        <DevolverCompra
          compra={devolviendo}
          proveedor={nombreProveedor(devolviendo.proveedorId)}
          onCerrar={() => setDevolviendo(null)}
        />
      )}
      {corrigiendo && (
        <CorregirCosto
          compra={corrigiendo}
          proveedor={nombreProveedor(corrigiendo.proveedorId)}
          onCerrar={() => setCorrigiendo(null)}
        />
      )}
      {contando && (
        <CorregirCantidad compra={contando} proveedor={nombreProveedor(contando.proveedorId)} onCerrar={() => setContando(null)} />
      )}
      {pagando && <CorregirPago compra={pagando} proveedor={nombreProveedor(pagando.proveedorId)} onCerrar={() => setPagando(null)} />}
      {/* A sheet, not a field that opened in the card and pushed the list down (the owner, 2026-10-06). */}
      {anulada && (
        <Modal
          titulo={t('compras.anularEsta')}
          onCerrar={() => {
            setAnulando(null);
            setError(null);
          }}
          pie={
            <button
              type="button"
              className="btn-primario w-full"
              disabled={!motivo.trim()}
              onClick={() => void anular(anulada.id)}
            >
              <Ico icono="anular" />
              {t('comun.anular')}
            </button>
          }
        >
          <div className="flex flex-col gap-4" data-compra-anular>
            <p className="text-sm text-slate-400">
              {nombreProveedor(anulada.proveedorId)}
              {anulada.documento ? ` · ${anulada.documento}` : ''} · {anulada.fecha} ·{' '}
              <span className="tabular">{formatMoney(anulada.totalCordobas)}</span>
            </p>
            <Campo
              maxLength={200}
              etiqueta={t('compras.motivoAnulacion')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              autoFocus
              autoComplete="off"
            />
            {error && <p className="text-sm text-rose-400">{error}</p>}
          </div>
        </Modal>
      )}
    </Pagina>
  );
}

/** Each way, short, in the line of a purchase's parts: its amount says C$ already. */
const PARTE: Record<string, Clave> = {
  efectivo_cordobas: 'compras.parteEfectivo',
  efectivo_dolares: 'pago.efectivo_dolares',
  transferencia: 'pago.transferencia',
  billetera: 'pago.billetera',
  credito: 'pago.credito',
};

/**
 * How a purchase paid in parts was paid (`conLaCompra`): each part, then what they left, the
 * purchase's own way: on credit, what was owed when the goods came, whatever has been paid since.
 * None for a purchase paid one way.
 */
function partesDeLaCompra(c: Compra, t: ReturnType<typeof useT>): { clave: string; como: string; monto: Centavos }[] {
  const partes = (c.pagos ?? []).filter((x) => x.conLaCompra && !x.anulado);
  if (partes.length === 0) return [];
  const resto = partes.reduce((a, x) => a - x.monto, c.totalCordobas as number);
  const nombre = (forma: string) => (PARTE[forma] ? t(PARTE[forma]!) : forma);
  return [
    ...partes.map((x) => ({ clave: x.id, como: `${nombre(x.formaPago)}${x.sesionId ? t('gastos.deLaGaveta') : ''}`, monto: x.monto })),
    ...(resto > 0
      ? [{ clave: 'resto', como: `${nombre(c.formaPago)}${c.sesionId ? t('gastos.deLaGaveta') : ''}`, monto: resto as Centavos }]
      : []),
  ];
}

/** The parts on the card: nothing for a purchase paid one way, nor once voided. */
function PagadoEnPartes({ compra: c }: { compra: Compra }) {
  const t = useT();
  const filas = c.anulada ? [] : partesDeLaCompra(c, t);
  if (filas.length === 0) return null;
  // A line each, the amount kept whole at its end: run together, «C$» broke from its figure.
  return (
    <ul className="tabular mt-2 flex flex-col gap-0.5 text-xs text-slate-400" data-compra-pagado-en-partes>
      {filas.map((f) => (
        <li key={f.clave} className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 wrap-break-word">{f.como}</span>
          <span className="shrink-0 whitespace-nowrap">{formatMoney(f.monto)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * What a purchase brought (the owner, 2026-10-05: «the history of all the entries we make in
 * Goods Bought»): each product with how much came, what one unit cost and the line as invoiced, in
 * the purchase's own currency; then the delivery cost, the total and how it was paid, and what is
 * still owed. Read live, so a return or a corrected cost shows the moment it is made. A supplier's
 * page opens its purchases on it too (`ProveedorDetalle.tsx`).
 */
export function DetalleCompra({ compraId, onCerrar }: { compraId: string; onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  const compra = useLiveQuery(() => db.compras.get(compraId), [compraId]);
  const proveedor = useLiveQuery(
    async () => (compra ? ((await db.proveedores.get(compra.proveedorId)) ?? null) : undefined),
    [compra?.proveedorId],
  );
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  if (!compra || proveedor === undefined) return null;

  const porId = new Map(productos.map((p) => [p.id, p]));
  const usd = compra.moneda === 'USD';
  // The lines as the invoice has them: dollars say so, córdobas go bare, as on a sale's lines.
  const enMoneda = (c: Centavos) => (usd ? `US$ ${formatMoney(c, { symbol: false })}` : formatMoney(c, { symbol: false }));
  const subtotal = compra.lineas.reduce<Centavos>((a, l) => addMoney(a, l.costoTotal), ZERO);
  const pendiente = pendienteDeCompra(compra);
  const partes = partesDeLaCompra(compra, t);
  const pagos =
    partes.length > 0
      ? partes
      : [
          {
            clave: 'forma',
            como: `${PAGO[compra.formaPago] ? t(PAGO[compra.formaPago]!) : compra.formaPago}${compra.sesionId ? t('gastos.deLaGaveta') : ''}`,
            monto: compra.totalCordobas,
          },
        ];

  return (
    <Modal titulo={proveedor?.nombre ?? t('gastos.proveedor')} onCerrar={onCerrar}>
      <p className="tabular -mt-1 mb-3 text-sm text-slate-400" data-compra-detalle>
        {compra.fecha}
        {compra.documento && ` · ${t('compra.factura')} ${compra.documento}`}
        {usd && compra.tasaCambio && t('compras.tasaUsd', { tasa: formatFxRate(compra.tasaCambio as never) })}
      </p>
      <ul className="mb-4 flex flex-col divide-y divide-slate-800" data-compra-lineas>
        {compra.lineas.map((l, i) => {
          const producto = porId.get(l.productoId);
          const unidad = producto ? etiquetaUnidad(producto.unidadBase, producto.unidadBase) : '';
          const unitario = costOf(unitCostFromTotal(l.costoTotal, l.cantidadBase), 1000 as Milli);
          return (
            <li key={i} className="flex items-center gap-3 py-2.5">
              {producto && <Miniatura producto={producto} tamano="sm" />}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{producto ? nombre(producto) : '—'}</p>
                <p className="tabular mt-0.5 text-xs text-slate-500">
                  {formatQuantity(l.cantidadBase)} {unidad} × {enMoneda(unitario)}
                  {(l.devuelta ?? 0) > 0 && (
                    <span className="ml-2 text-sky-300">{t('devolucion.yaDevuelto', { n: formatQuantity(l.devuelta!) })}</span>
                  )}
                </p>
              </div>
              <span className="tabular shrink-0 text-sm">{enMoneda(l.costoTotal)}</span>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-col gap-1 border-t border-slate-800 pt-3" data-compra-totales>
        {(compra.flete > 0 || (compra.iva ?? 0) > 0) && <Fila etiqueta={t('compra.subtotal')} valor={enMoneda(subtotal)} />}
        {compra.flete > 0 && <Fila etiqueta={t('compra.fleteCorto')} valor={enMoneda(compra.flete)} />}
        {(compra.iva ?? 0) > 0 && (
          <span data-compra-iva-detalle>
            <Fila etiqueta={t('compra.ivaCorto')} valor={enMoneda(compra.iva!)} />
          </span>
        )}
        <Fila
          etiqueta={t('comun.total')}
          valor={usd ? enMoneda(addMoney(addMoney(subtotal, compra.flete), compra.iva ?? ZERO)) : formatMoney(compra.totalCordobas)}
          fuerte
        />
        {usd && <Fila etiqueta={t('compra.equivaleA')} valor={formatMoney(compra.totalCordobas)} />}
        {/* Voided, nothing was paid: the payments went back with it. What was paid is in córdobas,
            which says so beside a purchase in dollars. */}
        {!compra.anulada &&
          pagos.map((x) => <Fila key={x.clave} etiqueta={x.como} valor={formatMoney(x.monto, { symbol: usd })} />)}
        {!compra.anulada && compra.formaPago === 'credito' && (
          <Fila etiqueta={t('compras.pagado')} valor={formatMoney(compra.pagado, { symbol: usd })} />
        )}
        {pendiente > 0 && <Fila etiqueta={t('compras.porPagar')} valor={formatMoney(pendiente)} fuerte />}
        {compra.vencePago && pendiente > 0 && (
          <p className="text-right text-xs text-amber-300">{t('compras.vence', { fecha: compra.vencePago })}</p>
        )}
        {(compra.devuelto ?? 0) > 0 && (
          <p className="tabular mt-1 text-xs text-sky-300">
            {t('devolucionCompra.devuelto', { monto: formatMoney(compra.devuelto!) })}
          </p>
        )}
      </div>

      {compra.anulada && (
        <p className="mt-4 rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
          {t('compras.anulada', { motivo: compra.motivoAnulacion ?? '' })}
        </p>
      )}
    </Modal>
  );
}

function Fila({ etiqueta, valor, fuerte }: { etiqueta: string; valor: string; fuerte?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={`min-w-0 text-sm ${fuerte ? 'font-medium' : 'text-slate-400'}`}>{etiqueta}</span>
      <span className={`tabular shrink-0 ${fuerte ? 'text-lg font-semibold' : 'text-sm'}`}>{valor}</span>
    </div>
  );
}

type Liquidacion = 'descontar' | 'efectivo_cordobas' | 'transferencia' | 'billetera';

const LIQUIDACIONES: { forma: Liquidacion; etiqueta: Clave; icono: NombreIcono }[] = [
  { forma: 'descontar', etiqueta: 'devolucionCompra.descontar', icono: 'compras' },
  { forma: 'efectivo_cordobas', etiqueta: 'pago.efectivo_cordobas', icono: 'caja' },
  { forma: 'transferencia', etiqueta: 'pago.transferencia', icono: 'transferencias' },
  { forma: 'billetera', etiqueta: 'pago.billetera', icono: 'billetera' },
];

/**
 * Goods going back to the supplier: expired biscuits the distributor takes back, a sack that
 * came torn. Each line is worth what it cost on this purchase, freight included, and the
 * supplier settles it off the bill when it is still owed or hands the money back when not.
 */
function DevolverCompra({
  compra,
  proveedor,
  onCerrar,
}: {
  compra: Compra;
  proveedor: string;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const porId = new Map(productos.map((p) => [p.id, p]));
  const existencias = useLiveQuery(
    () => db.existencias.where('ubicacion').equals(compra.ubicacion).toArray(),
    [compra.ubicacion],
    [],
  );
  const hay = new Map(existencias.map((e) => [e.productoId, e.cantidad]));
  const [cantidades, setCantidades] = useState<Record<number, string>>({});
  const [elegida, setElegida] = useState<Liquidacion | null>(null);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The córdoba account a refund by transfer comes into, once picked. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBancoCordobas | null>(null);
  const cuenta = cuentaEnCordobas(config, cuentaElegida);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendiente = pendienteDeCompra(compra);
  const formas = LIQUIDACIONES.filter((f) => f.forma !== 'descontar' || pendiente > 0);
  const liquidacion = elegida ?? (pendiente > 0 ? 'descontar' : 'efectivo_cordobas');

  const pedidas = compra.lineas.map((l, indice) => {
    const texto = (cantidades[indice] ?? '').trim();
    const cantidad = texto === '' ? ZERO_QTY : parseQuantity(texto);
    const queda = subQty(l.cantidadBase, l.devuelta ?? ZERO_QTY);
    return {
      indice,
      cantidad: cantidad ?? ZERO_QTY,
      queda,
      valida: cantidad !== null && cantidad >= 0 && cantidad <= queda,
    };
  });
  const aDevolver = pedidas.filter((p) => p.valida && p.cantidad > 0);
  const total = aDevolver.reduce<Centavos>((suma, p) => {
    const l = compra.lineas[p.indice]!;
    const desde = l.devuelta ?? ZERO_QTY;
    return addMoney(suma, valorDevueltoCompra(l, desde, addQty(desde, p.cantidad)));
  }, ZERO);
  const pasaDeLoQueSeDebe = liquidacion === 'descontar' && total > pendiente;
  const listo =
    aDevolver.length > 0 &&
    pedidas.every((p) => p.valida) &&
    !pasaDeLoQueSeDebe &&
    motivo.trim() !== '' &&
    !ocupado;

  async function registrar() {
    if (!listo) return;
    setOcupado(true);
    setError(null);
    const devolucionId = newId();
    await emitir('compra.devuelta', {
      devolucionId,
      compraId: compra.id,
      fecha: hoy(),
      lineas: aDevolver.map((p) => ({ indice: p.indice, cantidadBase: p.cantidad })),
      liquidacion,
      ...(liquidacion === 'transferencia' ? { cuenta } : {}),
      motivo: motivo.trim(),
    });
    if (await db.devolucionesCompra.get(devolucionId)) {
      onCerrar();
      return;
    }
    setOcupado(false);
    setError(t('devolucion.noSePudo'));
  }

  return (
    <Modal titulo={t('devolucionCompra.titulo', { proveedor })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-slate-400">
          {t('devolucionCompra.explicacion', { fecha: compra.fecha })}
        </p>

        <ul className="flex flex-col divide-y divide-slate-800">
          {compra.lineas.map((l, i) => {
            const producto = porId.get(l.productoId);
            const p = pedidas[i]!;
            if (p.queda <= 0) return null;
            const unidad = producto ? etiquetaUnidad(producto.unidadBase, producto.unidadBase) : '';
            const enExistencia = hay.get(l.productoId) ?? ZERO_QTY;
            return (
              <li key={i} className="flex flex-col gap-2 py-3">
                <div className="flex items-center gap-3">
                  {producto && <Miniatura producto={producto} tamano="sm" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{producto ? nombre(producto) : '—'}</p>
                    <p className="tabular mt-0.5 text-xs text-slate-500">
                      {t('devolucionCompra.recibido', { n: formatQuantity(l.cantidadBase), unidad })}
                      {(l.devuelta ?? 0) > 0 &&
                        ` · ${t('devolucion.yaDevuelto', { n: formatQuantity(l.devuelta!) })}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 pl-11">
                  <Campo
                    etiqueta={t('devolucion.cuanto')}
                    inputMode="decimal"
                    className="tabular"
                    value={cantidades[i] ?? ''}
                    placeholder="0"
                    onChange={(e) => setCantidades({ ...cantidades, [i]: e.target.value })}
                    onFocus={(e) => e.currentTarget.select()}
                    ayuda={
                      p.valida && p.cantidad > enExistencia
                        ? t('devolucionCompra.masQueExistencia', { n: formatQuantity(enExistencia) })
                        : undefined
                    }
                    error={p.valida ? undefined : t('devolucion.masDeLoQueQueda', { n: formatQuantity(p.queda) })}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn-secundario mt-6 shrink-0 px-3 text-sm"
                    onClick={() => setCantidades({ ...cantidades, [i]: formatQuantity(p.queda) })}
                  >
                    <Ico icono="marca" className="size-4" />
                    {t('devolucion.todo')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        <div>
          <span className="etiqueta">{t('devolucionCompra.como')}</span>
          <div className="grid grid-cols-2 gap-2">
            {formas.map((f) => (
              <button
                key={f.forma}
                type="button"
                onClick={() => setElegida(f.forma)}
                className={`flex min-h-12 items-center justify-center gap-2 rounded-xl px-2 py-1.5 text-sm font-medium transition ${
                  liquidacion === f.forma ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                <Ico icono={f.icono} className="size-5 shrink-0" />
                <span className="text-center leading-tight">{t(f.etiqueta)}</span>
              </button>
            ))}
          </div>
          <p className={`mt-2 text-xs ${pasaDeLoQueSeDebe ? 'text-rose-300' : 'text-slate-400'}`}>
            {liquidacion === 'descontar'
              ? t(pasaDeLoQueSeDebe ? 'devolucionCompra.pasaDeLoQueSeDebe' : 'devolucionCompra.deLaCuenta', {
                  monto: formatMoney(pendiente),
                })
              : t(
                  liquidacion === 'efectivo_cordobas'
                    ? 'devolucionCompra.alEfectivo'
                    : liquidacion === 'transferencia'
                      ? 'devolucionCompra.alBanco'
                      : 'devolucionCompra.aLaBilletera',
                )}
          </p>
          {liquidacion === 'transferencia' && (
            <div className="mt-3">
              <ElegirCuenta valor={cuenta} onCambiar={(c) => setCuentaElegida(c as CuentaBancoCordobas)} etiqueta={t('bancos.entraA')} />
            </div>
          )}
        </div>

        <Campo
          maxLength={200}
          etiqueta={t('comun.motivo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoComplete="off"
        />

        {error && <p className="text-sm text-rose-400">{error}</p>}

        <button type="button" className="btn-primario" disabled={!listo} onClick={() => void registrar()}>
          <Ico icono="devolver" />
          {total > 0 ? t('devolucion.devolverMonto', { monto: formatMoney(total) }) : t('devolucion.devolver')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Money a correction moves where it was counted since: what that count found, before and after
 * (`reencontrarElPago`), or, for a bank's or the wallet's statement, by how much what squaring it
 * found changes. Said without the count while it is read.
 */
function conElConteo(
  t: ReturnType<typeof useT>,
  como: string,
  encontrado: Reencontrado | { rechazo: string } | null | undefined,
  cuadre: boolean,
  monto: string,
): string {
  if (encontrado && !('rechazo' in encontrado)) return `${como} ${diceElConteo(t, encontrado)}`;
  if (encontrado === null && cuadre) return `${como} ${t('encontrado.cuadre', { monto })}`;
  return como;
}

/** A correction the count it reaches cannot take, as the sheet says it. */
function dichoElRechazo(t: ReturnType<typeof useT>, r: { rechazo: string }): string {
  if ('explicado' in r && 'faltaria' in r && typeof r.explicado === 'number' && typeof r.faltaria === 'number') {
    return t('conteoCaja.no.explicada', { explicado: formatMoney(centavos(r.explicado)), faltaria: formatMoney(centavos(r.faltaria)) });
  }
  return r.rechazo;
}

/** The account a purchase paid otherwise came out of, as the correction's explanation names it. */
const DE_CUENTA: Record<string, Clave> = {
  efectivo_cordobas: 'corregir.deEfectivo',
  efectivo_dolares: 'corregir.deEfectivo',
  transferencia: 'corregir.deBanco',
  billetera: 'corregir.deBilletera',
};

/**
 * A line's cost typed wrong — a case entered as units, a slipped digit — found once its goods have
 * moved and the purchase can no longer be voided. Before saving it says what the right cost makes
 * each unit and where the difference goes, the way the reducer (`compra.costo_corregido`) books it:
 * what is owed, the drawer, or the account it was paid from. A higher cost can leave prices under
 * the margin floor, so those come up next, to whoever sees the margins.
 */
function CorregirCosto({ compra, proveedor, onCerrar }: { compra: Compra; proveedor: string; onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const porId = new Map(productos.map((p) => [p.id, p]));
  const sesion = useLiveQuery(
    async () => (compra.sesionId ? ((await db.sesiones.get(compra.sesionId)) ?? null) : null),
    [compra.sesionId],
  );
  const [indice, setIndice] = useState<number | null>(compra.lineas.length === 1 ? 0 : null);
  const [costo, setCosto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revisando, setRevisando] = useState<string | null>(null);
  const verCostos = usePuede('ver_costos');
  const config = useLiveQuery(() => readSettings(), []);

  const moneda = compra.moneda === 'USD' ? 'US$' : 'C$';
  const enMoneda = (c: Centavos) => `${moneda} ${formatMoney(c, { symbol: false })}`;
  const linea = indice === null ? undefined : compra.lineas[indice];
  const producto = linea ? porId.get(linea.productoId) : undefined;
  const unidad = producto ? etiquetaUnidad(producto.unidadBase, producto.unidadBase) : '';
  const nuevo = parseMoney(costo);
  const tasa = compra.moneda === 'USD' && compra.tasaCambio ? fxRate(compra.tasaCambio) : null;
  const nuevoCordobas =
    linea && nuevo !== null && nuevo >= 0
      ? // Past what the books take — a code scanned into the cost — no figure, not a crash.
        enRango(() =>
          tasa
            ? usdToCordobas(addMoney(addMoney(nuevo, linea.fleteAsignado), linea.ivaAsignado ?? ZERO), tasa)
            : addMoney(addMoney(nuevo, linea.fleteAsignado), linea.ivaAsignado ?? ZERO),
        )
      : null;
  const diferencia = linea && nuevoCordobas !== null ? subMoney(nuevoCordobas, linea.costoCordobas) : null;
  // Counted since, what that count found moves instead: read as the reducer will read it.
  const encontrado = useLiveQuery(
    async () =>
      diferencia !== null && diferencia !== 0 && sesion !== undefined && config
        ? reencontrarElPago(localReader, config, adondeVaLaCorreccion(compra, sesion ?? undefined, config), compra, -diferencia, Infinity)
        : null,
    [compra, sesion, config, diferencia],
  );
  const noCuadra = encontrado && 'rechazo' in encontrado ? encontrado : null;
  // The reducer refuses this, and a refusal says nothing on screen: checked here first.
  const bajoLoAbonado =
    diferencia !== null &&
    compra.formaPago === 'credito' &&
    addMoney(compra.totalCordobas, diferencia) < compra.pagado + (compra.devueltoACuenta ?? 0);
  const listo = diferencia !== null && diferencia !== 0 && !bajoLoAbonado && !noCuadra && motivo.trim() !== '' && !ocupado;
  const porUnidad = (c: Centavos) =>
    linea && linea.cantidadBase > 0
      ? t('compra.costoPorUnidad', {
          monto: formatMoney(costOf(unitCostFromTotal(c, linea.cantidadBase), 1000 as Milli)),
          unidad,
        })
      : '';

  // Where the difference goes, as the books will put it (`adondeVaLaCorreccion`).
  let destino: string | null = null;
  if (diferencia !== null && diferencia !== 0 && sesion !== undefined && config) {
    const monto = formatMoney(centavos(Math.abs(diferencia)));
    const mas = diferencia > 0;
    const { destino: donde, cuenta: cuentaDe } = adondeVaLaCorreccion(compra, sesion ?? undefined, config);
    const cuenta = t(DE_CUENTA[compra.formaPago] ?? 'corregir.deEfectivo');
    if (donde === 'deuda') destino = t(mas ? 'corregir.debeMas' : 'corregir.debeMenos', { monto });
    else if (donde === 'gaveta') destino = t(mas ? 'corregir.gavetaMas' : 'corregir.gavetaMenos', { monto });
    else if (donde === 'corte') destino = conElConteo(t, t('corregir.corte'), encontrado, false, monto);
    else if (donde === 'conteo') destino = conElConteo(t, t('corregir.conteo', { cuenta }), encontrado, cuentaDe !== 'CAJA_FUERTE', monto);
    else destino = t(mas ? 'corregir.cuentaMas' : 'corregir.cuentaMenos', { monto, cuenta });
  }

  async function corregir() {
    if (!listo || indice === null || !linea || nuevo === null) return;
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('compra.costo_corregido', {
        compraId: compra.id,
        indice,
        costoTotal: nuevo,
        motivo: motivo.trim(),
      });
      const despues = await db.compras.get(compra.id);
      if (rechazo || despues?.lineas[indice]?.costoTotal !== nuevo) {
        setOcupado(false);
        setError(rechazo ?? t('corregir.noSePudo'));
        return;
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('corregir.noSePudo'));
      return;
    }
    if (verCostos && diferencia! > 0 && (await buscarPreciosBajos([linea.productoId])).length > 0) {
      setRevisando(linea.productoId);
      return;
    }
    onCerrar();
  }

  if (revisando) return <PreciosBajoMargen soloProductos={[revisando]} onCerrar={onCerrar} />;

  return (
    <Modal titulo={t('corregir.titulo', { proveedor })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-slate-400">
          {t('corregir.explicacion', { fecha: compra.fecha })}
        </p>

        <ul className="flex flex-col divide-y divide-slate-800">
          {compra.lineas.map((l, i) => {
            const p = porId.get(l.productoId);
            const conDevolucion = (l.devuelta ?? 0) > 0;
            const elegida = indice === i;
            return (
              <li key={i}>
                <button
                  type="button"
                  disabled={conDevolucion}
                  aria-pressed={elegida}
                  data-sin-icono="fila"
                  onClick={() => {
                    setIndice(i);
                    setCosto('');
                    setError(null);
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left transition ${
                    elegida ? 'bg-marca-600/15 ring-1 ring-marca-500/40' : 'enabled:hover:bg-slate-800'
                  } ${conDevolucion ? 'opacity-50' : ''}`}
                >
                  {p && <Miniatura producto={p} tamano="sm" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{p ? nombre(p) : '—'}</span>
                    <span className="tabular block text-xs text-slate-500">
                      {formatQuantity(l.cantidadBase)} {p ? etiquetaUnidad(p.unidadBase, p.unidadBase) : ''} ·{' '}
                      {enMoneda(l.costoTotal)}
                    </span>
                    {conDevolucion && <span className="block text-xs text-slate-500">{t('corregir.conDevolucion')}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {linea && (
          <>
            <Campo
              etiqueta={t('corregir.costoCorrecto', { moneda })}
              inputMode="decimal"
              className="tabular"
              value={costo}
              placeholder={formatMoney(linea.costoTotal, { symbol: false })}
              onChange={(e) => setCosto(e.target.value)}
              ayuda={t('corregir.antes', { costo: porUnidad(linea.costoCordobas) })}
              error={bajoLoAbonado ? t('corregir.bajoLoAbonado') : undefined}
              autoComplete="off"
            />
            {diferencia !== null && diferencia !== 0 && nuevoCordobas !== null && (
              <div className="rounded-lg bg-slate-800/60 px-3 py-2 text-sm">
                <p className="tabular">
                  {porUnidad(nuevoCordobas)}
                  <span className={diferencia > 0 ? 'text-amber-200' : 'text-sky-300'}>
                    {' · '}
                    {formatMoney(diferencia, { signed: true })}
                  </span>
                </p>
                {destino && <p className="mt-1 text-xs text-slate-400">{destino}</p>}
                {noCuadra && <p className="mt-1 text-xs text-rose-400">{dichoElRechazo(t, noCuadra)}</p>}
              </div>
            )}
            <Campo
              maxLength={200}
              etiqueta={t('comun.motivo')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              autoComplete="off"
            />
          </>
        )}

        {error && <p className="text-sm text-rose-400">{error}</p>}

        <button type="button" className="btn-primario" disabled={!listo} onClick={() => void corregir()}>
          <Ico icono="guardar" />
          {t('corregir.guardar')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * A line's quantity typed wrong — a sack entered as one unit — found once its goods have moved (the
 * owner, 2026-10-13). Before saving it says what each unit really cost, and what that does to the
 * sales that took the purchase's cost, to the stock, and to what a count since found, from the same
 * arithmetic the books use (`corregirCantidadDeCompra`). No money moves. A higher cost a unit can
 * leave prices under the margin floor, so those come up next, to whoever sees the margins.
 */
function CorregirCantidad({ compra: alAbrir, proveedor, onCerrar }: { compra: Compra; proveedor: string; onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  // Read live: another correction, or a sale, changes what this one does.
  const compra = useLiveQuery(() => db.compras.get(alAbrir.id), [alAbrir.id]) ?? alAbrir;
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const porId = new Map(productos.map((p) => [p.id, p]));
  const [indice, setIndice] = useState<number | null>(compra.lineas.length === 1 ? 0 : null);
  const [cantidad, setCantidad] = useState('');
  const [unidadId, setUnidadId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revisando, setRevisando] = useState<string | null>(null);
  const verCostos = usePuede('ver_costos');

  const linea = indice === null ? undefined : compra.lineas[indice];
  const productoId = linea?.productoId;
  const producto = productoId ? porId.get(productoId) : undefined;
  const unidad = producto ? etiquetaUnidad(producto.unidadBase, producto.unidadBase) : '';
  const presentaciones = useLiveQuery(
    async () => (productoId ? unidadesDe(await db.unidades.where('productoId').equals(productoId).toArray(), productoId) : []),
    [productoId],
    [],
  );
  // What the books will read: the stock where it went, and the sales from its day on.
  const leido = useLiveQuery(
    async () =>
      productoId
        ? {
            productoId,
            existencia: await localReader.existencia(productoId, compra.ubicacion),
            ventas: await localReader.ventasDeProducto(productoId, compra.ubicacion, compra.fecha),
            compras: await localReader.comprasDeProducto(productoId, compra.ubicacion),
          }
        : null,
    [productoId, compra.ubicacion, compra.fecha],
  );

  const elegida = presentaciones.find((u) => u.id === unidadId);
  const escrita = parseQuantity(cantidad);
  const cantidadBase = escrita !== null && escrita > 0 ? (elegida ? enRango(() => saleToBase(escrita, elegida.factor)) : escrita) : null;
  const plan =
    linea && indice !== null && cantidadBase !== null && cantidadBase > 0 && leido && leido.productoId === productoId
      ? enRango(() => corregirCantidadDeCompra(compra, indice, cantidadBase, leido.existencia, leido.ventas, leido.compras))
      : null;
  const hecho = plan && !('rechazo' in plan) ? plan : null;
  const misma = cantidadBase !== null && linea !== undefined && cantidadBase === linea.cantidadBase;
  // A later purchase that left the same cost goes first: its sales and this one's look alike.
  const posterior = plan && 'rechazo' in plan ? plan.posterior : undefined;
  const listo = hecho !== null && motivo.trim() !== '' && !ocupado;
  const cada = (c: UnitCost) => formatMoney(costOf(c, 1000 as Milli));

  let conteo: string | null = null;
  if (hecho && hecho.contado && hecho.aConteo !== 0) {
    const monto = formatMoney(centavos(Math.abs(hecho.aConteo)));
    const baja = hecho.cuentaConteo === 'SOBRANTE_INVENTARIO' ? 0 < hecho.aConteo : hecho.aConteo < 0;
    const clave = hecho.cuentaConteo === 'SOBRANTE_INVENTARIO' ? (baja ? 'cantidad.sobranteBaja' : 'cantidad.sobranteSube') : baja ? 'cantidad.faltanteBaja' : 'cantidad.faltanteSube';
    conteo = t(clave, { monto });
  }
  const enVentas = hecho ? hecho.ventas.reduce((a, v) => a + v.diferencia, 0) : 0;

  async function corregir() {
    if (!listo || indice === null || !linea || cantidadBase === null || !hecho) return;
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('compra.cantidad_corregida', {
        compraId: compra.id,
        indice,
        cantidadBase,
        motivo: motivo.trim(),
      });
      const despues = await db.compras.get(compra.id);
      if (rechazo || despues?.lineas[indice]?.cantidadBase !== cantidadBase) {
        setOcupado(false);
        setError(rechazo ?? t('cantidad.noSePudo'));
        return;
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('cantidad.noSePudo'));
      return;
    }
    if (verCostos && hecho.costoDespues > hecho.costoAntes && (await buscarPreciosBajos([linea.productoId])).length > 0) {
      setRevisando(linea.productoId);
      return;
    }
    onCerrar();
  }

  if (revisando) return <PreciosBajoMargen soloProductos={[revisando]} onCerrar={onCerrar} />;

  return (
    <Modal titulo={t('cantidad.titulo', { proveedor })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5" data-corregir-cantidad-hoja>
        <p className="text-sm text-slate-400">{t('cantidad.explicacion', { fecha: compra.fecha })}</p>

        <ul className="flex flex-col divide-y divide-slate-800">
          {compra.lineas.map((l, i) => {
            const p = porId.get(l.productoId);
            const conDevolucion = (l.devuelta ?? 0) > 0;
            const esta = indice === i;
            return (
              <li key={i}>
                <button
                  type="button"
                  disabled={conDevolucion}
                  aria-pressed={esta}
                  data-sin-icono="fila"
                  onClick={() => {
                    setIndice(i);
                    setCantidad('');
                    setUnidadId('');
                    setError(null);
                  }}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left transition ${
                    esta ? 'bg-marca-600/15 ring-1 ring-marca-500/40' : 'enabled:hover:bg-slate-800'
                  } ${conDevolucion ? 'opacity-50' : ''}`}
                >
                  {p && <Miniatura producto={p} tamano="sm" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{p ? nombre(p) : '—'}</span>
                    <span className="tabular block text-xs text-slate-500">
                      {formatQuantity(l.cantidadBase)} {p ? etiquetaUnidad(p.unidadBase, p.unidadBase) : ''} ·{' '}
                      {formatMoney(l.costoCordobas)}
                    </span>
                    {conDevolucion && <span className="block text-xs text-slate-500">{t('corregir.conDevolucion')}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {linea && (
          <>
            <div className={presentaciones.length > 1 ? 'grid grid-cols-2 gap-3' : ''}>
              <Campo
                etiqueta={t('cantidad.correcta')}
                inputMode="decimal"
                className="tabular"
                value={cantidad}
                placeholder={formatQuantity(linea.cantidadBase)}
                onChange={(e) => setCantidad(e.target.value)}
                ayuda={t('cantidad.ahora', {
                  cantidad: `${formatQuantity(linea.cantidadBase)} ${unidad}`,
                  costo: formatMoney(costOf(unitCostFromTotal(linea.costoCordobas, linea.cantidadBase), 1000 as Milli)),
                  unidad,
                })}
                error={misma ? t('cantidad.misma') : posterior ? t('cantidad.otraAlMismoCosto', { fecha: posterior.fecha }) : undefined}
                autoComplete="off"
                data-cantidad-correcta
              />
              {presentaciones.length > 1 && (
                <Selector etiqueta={t('compra.unidad')} value={elegida?.id ?? ''} onChange={(e) => setUnidadId(e.target.value)}>
                  {presentaciones.map((u) => (
                    <option key={u.id} value={u.factor === 1000 ? '' : u.id}>
                      {u.nombre}
                    </option>
                  ))}
                </Selector>
              )}
            </div>
            {hecho && (
              <div className="flex flex-col gap-1 rounded-lg bg-slate-800/60 px-3 py-2 text-sm" data-cantidad-efecto>
                <p className="tabular">{t('cantidad.cadaUnidad', { unidad, antes: cada(hecho.costoAntes), despues: cada(hecho.costoDespues) })}</p>
                {hecho.ventas.length > 0 && enVentas !== 0 && (
                  <p className="text-xs text-slate-300" data-cantidad-ventas>
                    {t(enVentas < 0 ? 'cantidad.ventasBajan' : 'cantidad.ventasSuben', {
                      n: hecho.ventas.length,
                      monto: formatMoney(centavos(Math.abs(enVentas))),
                    })}
                  </p>
                )}
                <p className="tabular text-xs text-slate-400" data-cantidad-estante>
                  {hecho.contado
                    ? t('cantidad.contado', {
                        cantidad: formatQuantity(hecho.estante.despues.cantidad),
                        unidad,
                        valorAntes: formatMoney(costOf(hecho.estante.antes.costoPromedio, hecho.estante.antes.cantidad)),
                        valorDespues: formatMoney(costOf(hecho.estante.despues.costoPromedio, hecho.estante.despues.cantidad)),
                      })
                    : t('cantidad.estante', {
                        antes: formatQuantity(hecho.estante.antes.cantidad),
                        despues: formatQuantity(hecho.estante.despues.cantidad),
                        unidad,
                        valorAntes: formatMoney(costOf(hecho.estante.antes.costoPromedio, hecho.estante.antes.cantidad)),
                        valorDespues: formatMoney(costOf(hecho.estante.despues.costoPromedio, hecho.estante.despues.cantidad)),
                      })}
                </p>
                {conteo && <p className="text-xs text-slate-400" data-cantidad-conteo>{conteo}</p>}
                {!hecho.soloEsta && (
                  <p className="text-xs text-amber-200" data-cantidad-mezclado>
                    {t('cantidad.mezclado', { costo: cada(hecho.estante.despues.costoPromedio), unidad })}
                  </p>
                )}
                <p className="text-xs text-slate-500">{t('cantidad.sinDinero')}</p>
              </div>
            )}
            <Campo
              maxLength={200}
              etiqueta={t('comun.motivo')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              autoComplete="off"
            />
          </>
        )}

        {error && <p className="text-sm text-rose-400">{error}</p>}

        <button type="button" className="btn-primario" disabled={!listo} onClick={() => void corregir()}>
          <Ico icono="guardar" />
          {t('cantidad.guardar')}
        </button>
      </div>
    </Modal>
  );
}

/** What stops a purchase's payment from being corrected at all, said instead of the form. */
const PAGO_NO_SE_CORRIGE: Partial<Record<PorQueElPagoNo, Clave>> = {
  enDolares: 'pagoCorregido.enDolares',
  enPartes: 'pagoCorregido.enPartes',
  conDevoluciones: 'pagoCorregido.conDevoluciones',
  conAbonos: 'pagoCorregido.conAbonos',
};

/**
 * How a purchase was paid, written down wrong — cash that went by transfer, paid when it was on
 * credit — found once the money has been counted (the owner, 2026-10-14). The same choices as
 * «Nueva compra», the drawer too while the till that was open the day it came is open on this
 * phone. Before saving it says what the books do with each side, from the reading they use
 * (`corregirFormaDePago`): what is owed, the drawer, the account, or what a count since found.
 */
function CorregirPago({ compra: alAbrir, proveedor, onCerrar }: { compra: Compra; proveedor: string; onCerrar: () => void }) {
  const t = useT();
  // Read live: corrected from the other phone meanwhile, the sheet says it at once.
  const compra = useLiveQuery(() => db.compras.get(alAbrir.id), [alAbrir.id]) ?? alAbrir;
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const antes = useLiveQuery(
    async () => (compra.sesionId ? ((await db.sesiones.get(compra.sesionId)) ?? null) : null),
    [compra.sesionId],
  );
  const caja = useLiveQuery(() => sesionAbierta(), [], undefined);
  const [formaPago, setFormaPago] = useState<FormaPago>(alAbrir.formaPago);
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBanco | null>(
    alAbrir.formaPago === 'transferencia' ? ((alAbrir.cuenta ?? 'BANCOS') as CuentaBanco) : null,
  );
  const [vence, setVence] = useState(alAbrir.vencePago ?? '');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The drawer that could have paid it: this phone's, open at its place since the day it came in.
  const dia = compra.momento != null ? fechaEnNicaragua(new Date(compra.momento)) : compra.fecha;
  const cajaDeEseDia = caja && caja.ubicacion === compra.ubicacion && fechaEnNicaragua(caja.abiertaEn) <= dia ? caja : undefined;
  const gaveta = useGaveta(formaPago === 'efectivo_cordobas' && cajaDeEseDia ? 'efectivo_cordobas' : '', { encendidaAlEmpezar: false });
  // Paid out of this very drawer, the switch says so once the till is read.
  const deEstaGaveta = Boolean(alAbrir.sesionId) && alAbrir.sesionId === cajaDeEseDia?.id;
  const { setEncendido } = gaveta;
  useEffect(() => {
    if (deEstaGaveta) setEncendido(true);
  }, [deEstaGaveta, setEncendido]);
  const cuenta = formaPago === 'transferencia' ? cuentaEnCordobas(config, cuentaElegida as CuentaBancoCordobas | null) : null;
  const nuevo: PagoCorregido = {
    formaPago,
    sesionId: gaveta.sesionId ?? null,
    cuenta,
    vencePago: formaPago === 'credito' ? vence || null : null,
  };
  const plan =
    antes === undefined
      ? null
      : corregirFormaDePago(compra, nuevo, { antes: antes ?? undefined, despues: gaveta.sesionId ? cajaDeEseDia : undefined }, config);
  const hecho = plan && !('rechazo' in plan) ? plan : null;
  const definitivo = plan && 'rechazo' in plan ? PAGO_NO_SE_CORRIGE[plan.por] : undefined;
  // Counted since, what those counts found moves instead: read as the reducer will read it.
  const encontrados = useLiveQuery(
    async () => (hecho ? reencontrarLosPagos(localReader, config, hecho, compra, nuevo.sesionId ?? null, Infinity) : null),
    [hecho?.vuelve.destino, hecho?.vuelve.cuenta, hecho?.sale.destino, hecho?.sale.cuenta, hecho?.monto, hecho?.mismaPlata, compra, nuevo.sesionId, config],
  );
  const noCuadra = encontrados && 'rechazo' in encontrados ? encontrados : null;
  const sinGaveta = faltaEnGaveta(gaveta, compra.totalCordobas, t);
  const listo = hecho !== null && !noCuadra && motivo.trim() !== '' && !ocupado && !sinGaveta;

  const monto = formatMoney(compra.totalCordobas);
  /** Where a payment came from, as the sentences say it: «del efectivo general», «de BAC córdobas». */
  const deDonde = (forma: FormaPago, suya: string | null | undefined, deLaGaveta: boolean) => {
    if (deLaGaveta) return t('pagoCorregido.deGaveta');
    if (forma !== 'transferencia') return t(DE_CUENTA[forma] ?? 'corregir.deEfectivo');
    const nombre = nombrePuesto(config, suya ?? 'BANCOS');
    return nombre ? t('pagoCorregido.deCuenta', { nombre }) : t('corregir.deBanco');
  };
  const dice = (lado: LadoDelPago, vuelve: boolean, forma: FormaPago, suya: string | null | undefined) => {
    const cuentaDicha = deDonde(forma, suya, false);
    const encontrado = encontrados && !('rechazo' in encontrados) ? (vuelve ? encontrados.vuelveA : encontrados.saleDe) : undefined;
    switch (lado.destino) {
      case 'deuda':
        return vuelve ? t('pagoCorregido.vuelveDeuda', { monto }) : t('pagoCorregido.saleDeuda', { monto, fecha: vence });
      case 'gaveta':
        return t(vuelve ? 'pagoCorregido.vuelveGaveta' : 'pagoCorregido.saleGaveta', { monto });
      case 'corte':
        return conElConteo(t, t(vuelve ? 'pagoCorregido.vuelveCorte' : 'pagoCorregido.saleCorte'), encontrado, false, monto);
      case 'conteo':
        return conElConteo(
          t,
          t(vuelve ? 'pagoCorregido.vuelveConteo' : 'pagoCorregido.saleConteo', { cuenta: cuentaDicha }),
          encontrado,
          lado.cuenta !== 'CAJA_FUERTE',
          monto,
        );
      default:
        return t(vuelve ? 'pagoCorregido.vuelveCuenta' : 'pagoCorregido.saleCuenta', { monto, cuenta: cuentaDicha });
    }
  };
  const comoDice = [
    PAGO[compra.formaPago] ? t(PAGO[compra.formaPago]!) : compra.formaPago,
    compra.sesionId || compra.formaPago === 'transferencia' ? deDonde(compra.formaPago, compra.cuenta, Boolean(compra.sesionId)) : null,
    compra.formaPago === 'credito' && compra.vencePago ? t('compras.vence', { fecha: compra.vencePago }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  async function corregir() {
    if (!listo) return;
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('compra.forma_pago_corregida', {
        compraId: compra.id,
        formaPago,
        ...(gaveta.sesionId ? { sesionId: gaveta.sesionId } : {}),
        ...(cuenta ? { cuenta } : {}),
        ...(formaPago === 'credito' ? { vencePago: vence } : {}),
        motivo: motivo.trim(),
      });
      const despues = await db.compras.get(compra.id);
      const tomada =
        despues?.formaPago === formaPago && (despues.sesionId ?? null) === (gaveta.sesionId ?? null) && (despues.cuenta ?? null) === (cuenta ?? null);
      if (rechazo || !tomada) {
        setOcupado(false);
        setError(rechazo ?? t('pagoCorregido.noSePudo'));
        return;
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('pagoCorregido.noSePudo'));
      return;
    }
    onCerrar();
  }

  return (
    <Modal titulo={t('pagoCorregido.titulo', { proveedor })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5" data-corregir-pago-hoja>
        <p className="text-sm text-slate-300" data-pago-dice>
          {t('pagoCorregido.dice', { forma: comoDice })}
        </p>
        {definitivo ? (
          <p className="text-sm text-amber-200" data-pago-no-se-corrige>
            {t(definitivo)}
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-400">{t('pagoCorregido.explicacion', { fecha: compra.fecha, monto })}</p>
            <Selector
              etiqueta={t('pagoCorregido.deVerdad')}
              value={formaPago}
              onChange={(e) => {
                setFormaPago(e.target.value as FormaPago);
                setError(null);
              }}
            >
              <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
              <option value="efectivo_dolares">{t('gastos.efectivoUsd')}</option>
              <option value="transferencia">{t('pago.transferencia')}</option>
              <option value="billetera">{t('gastos.billeteraMovil')}</option>
              <option value="credito">{t('compra.alCredito')}</option>
            </Selector>
            {formaPago === 'transferencia' && cuenta && (
              <ElegirCuenta valor={cuenta} onCambiar={setCuentaElegida} etiqueta={t('bancos.pagaDe')} />
            )}
            {formaPago === 'credito' && (
              <Campo etiqueta={t('compra.vence')} type="date" value={vence} onChange={(e) => setVence(e.target.value)} />
            )}
            <SaleDeLaGaveta gaveta={gaveta} />
            {sinGaveta && <p className="text-sm text-rose-400">{sinGaveta}</p>}
            {hecho && (
              <div className="rounded-lg bg-slate-800/60 px-3 py-2 text-sm" data-pago-libros>
                {hecho.mismaPlata ? (
                  <p className="text-xs text-slate-400">{t('pagoCorregido.mismaPlata')}</p>
                ) : (
                  <>
                    <p className="text-xs text-slate-400" data-pago-vuelve>
                      {dice(hecho.vuelve, true, compra.formaPago, compra.cuenta)}
                    </p>
                    <p className="mt-1 text-xs text-slate-400" data-pago-sale>
                      {dice(hecho.sale, false, formaPago, cuenta)}
                    </p>
                    {noCuadra && <p className="mt-1 text-xs text-rose-400">{dichoElRechazo(t, noCuadra)}</p>}
                  </>
                )}
              </div>
            )}
            <Campo
              maxLength={200}
              etiqueta={t('comun.motivo')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              autoComplete="off"
            />
          </>
        )}

        {error && <p className="text-sm text-rose-400">{error}</p>}

        {!definitivo && (
          <button type="button" className="btn-primario" disabled={!listo} onClick={() => void corregir()}>
            <Ico icono="guardar" />
            {t('pagoCorregido.guardar')}
          </button>
        )}
      </div>
    </Modal>
  );
}
